import React, { useRef, useEffect, useState } from 'react';
import * as Styled from './ViewportOptimizedWrappers.styled';
/**
 * Enhanced InView hook based on the existing Carousel InView component.
 *
 * Modified to support mount/unmount behavior for viewport optimization
 * and reduce DOM nodes when not in viewport.
 *
 *  Example: ProductCard images and swatches
 *
 */
var useInViewOptimization = function (
/**
 * Root margin for the Intersection Observer.
 * This is used to trigger the observer before the element is fully in view.
 */
rootMargin, 
/**
 * Threshold for the Intersection Observer.
 * Can be a single number or an array of numbers.
 * Determines at what percentage of visibility the observer callback is triggered.
 */
threshold) {
    if (rootMargin === void 0) { rootMargin = '150px'; }
    if (threshold === void 0) { threshold = [0]; }
    var inViewRef = useRef(null);
    var _a = useState(true), shouldRender = _a[0], setShouldRender = _a[1];
    var _b = useState(false), hydrated = _b[0], setHydrated = _b[1];
    var timeoutRef = useRef(null);
    // Ensures server-rendered markup matches client-side output to avoid hydration issues.
    // Once application has mounted, we can defer to client-only logic.
    useEffect(function () {
        setHydrated(true);
    }, []);
    useEffect(function () {
        var element = inViewRef.current;
        if (window.IntersectionObserver && element) {
            var observer_1 = new IntersectionObserver(function (entries) {
                entries.forEach(function (entry) {
                    if (timeoutRef.current) {
                        clearTimeout(timeoutRef.current);
                        timeoutRef.current = null;
                    }
                    if (entry.isIntersecting) {
                        setShouldRender(true);
                    }
                    else {
                        timeoutRef.current = setTimeout(function () {
                            setShouldRender(false);
                        }, 300);
                    }
                });
            }, {
                root: null,
                rootMargin: rootMargin,
                threshold: Array.isArray(threshold) ? threshold : [threshold]
            });
            observer_1.observe(element);
            return function () {
                observer_1.unobserve(element);
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                }
            };
        }
    }, [rootMargin, threshold, hydrated]);
    return {
        ref: inViewRef,
        shouldRender: shouldRender || !hydrated
    };
};
/**
 * Wrapper for ImageContainer that maintains dimensions when not in viewport
 */
export var ViewportOptimizedImageContainer = function (_a) {
    var children = _a.children;
    var _b = useInViewOptimization('100px'), ref = _b.ref, shouldRender = _b.shouldRender;
    return (React.createElement("div", { ref: ref }, shouldRender ? children : React.createElement(Styled.ImageContainerPlaceholder, null)));
};
/**
 * Wrapper for ColorSelection that maintains dimensions when not in viewport
 */
export var ViewportOptimizedColorSelection = function (_a) {
    var children = _a.children;
    var _b = useInViewOptimization('100px'), ref = _b.ref, shouldRender = _b.shouldRender;
    return (React.createElement("div", { ref: ref }, shouldRender ? children : React.createElement(Styled.ColorSelectionPlaceholder, null)));
};
